import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle, XCircle, Clock, FileText, Star, Shield, CreditCard,
  Banknote, MessageCircle, ChevronDown, ChevronUp, Search,
  AlertCircle, Pause, X, Send, Inbox,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient } from '../services/api';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';
import type { PaymentVerificationItem } from '../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

type ApprovalCategory = 'document' | 'rating' | 'verification' | 'credit' | 'payment' | 'message';

interface PendingDoc {
  documentId: string;
  userId: string;
  documentType: string;
  originalFileName: string;
  contentType: string | null;
  fileSizeBytes: number | null;
  expiryDate: string | null;
  uploadedAt: string;
  reviewStatus: string;
  reviewNotes: string | null;
}

interface PendingRating {
  uploadId: string;
  userId: string;
  platform: string;
  userRole: string;
  originalFileName: string;
  status: string;
  uploadedAt: string;
  screenshotSasUrl: string | null;
  aiExtractedRating: number | null;
}

interface PendingVerification {
  userId: string;
  email: string;
  fullName: string;
  roleName: string;
  registrationStatus: string;
  pendingDocuments: number;
  createdAt: string;
}

interface CreditRequest {
  userId: string;
  fullName: string;
  email: string;
  isBusinessAccount: boolean;
  businessName?: string;
  creditApprovalStatus: string;
  requestedAt?: string;
}

interface MailboxItem {
  id: string;
  fromAddress: string;
  fromName?: string;
  userId?: string;
  source?: string;
  subject: string;
  bodyText?: string;
  receivedAt: string;
  status: number;
}

type RawEntry = PendingDoc | PendingRating | PendingVerification | CreditRequest | PaymentVerificationItem | MailboxItem;

interface QueueItem {
  id: string;
  category: ApprovalCategory;
  subType: string;
  userId: string;
  userName: string;
  userEmail: string;
  summary: string;
  submittedAt: string;
  raw: RawEntry;
}

interface UserGroup {
  key: string;
  userId: string;
  userName: string;
  userEmail: string;
  items: QueueItem[];
  categories: ApprovalCategory[];
  oldest: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const DOC_LABELS: Record<string, string> = {
  HireRewardInsurance:      'H&R Insurance',
  GoodsInTransitInsurance:  'GIT Insurance',
  PublicLiabilityInsurance: 'Public Liability',
  MotCertificate:           'MOT Certificate',
  DrivingLicence:           'Driving Licence',
  IdentityDocument:         'Identity Document',
};

const CATEGORY_LABEL: Record<ApprovalCategory | 'all', string> = {
  all:          'All',
  document:     'Documents',
  rating:       'Ratings',
  verification: 'Verification',
  credit:       'Credit',
  payment:      'Payments',
  message:      'Messages',
};

const PAYMENT_STATUS_LABEL: Record<number, string> = {
  1: 'Pending', 2: 'Processing', 3: 'Released', 7: 'Ops Approved',
};

function fmt(n: number) { return `£${n.toFixed(2)}`; }

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: '2-digit',
  });
}

function fmtDateTime(s: string) {
  const d = new Date(s);
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

// ── Category UI config ────────────────────────────────────────────────────────

const CAT_CONFIG: Record<ApprovalCategory, {
  icon: React.ReactNode;
  smallIcon: React.ReactNode;
  pillBg: string;
  pillText: string;
  boxBg: string;
  boxText: string;
}> = {
  document: {
    icon:      <FileText className="w-3.5 h-3.5" />,
    smallIcon: <FileText className="w-3 h-3" />,
    pillBg:  'bg-blue-100',   pillText:  'text-blue-700',
    boxBg:   'bg-blue-50',    boxText:   'text-blue-600',
  },
  rating: {
    icon:      <Star className="w-3.5 h-3.5" />,
    smallIcon: <Star className="w-3 h-3" />,
    pillBg:  'bg-amber-100',  pillText:  'text-amber-700',
    boxBg:   'bg-amber-50',   boxText:   'text-amber-600',
  },
  verification: {
    icon:      <Shield className="w-3.5 h-3.5" />,
    smallIcon: <Shield className="w-3 h-3" />,
    pillBg:  'bg-green-100',  pillText:  'text-green-700',
    boxBg:   'bg-green-50',   boxText:   'text-green-600',
  },
  credit: {
    icon:      <CreditCard className="w-3.5 h-3.5" />,
    smallIcon: <CreditCard className="w-3 h-3" />,
    pillBg:  'bg-purple-100', pillText:  'text-purple-700',
    boxBg:   'bg-purple-50',  boxText:   'text-purple-600',
  },
  payment: {
    icon:      <Banknote className="w-3.5 h-3.5" />,
    smallIcon: <Banknote className="w-3 h-3" />,
    pillBg:  'bg-indigo-100', pillText:  'text-indigo-700',
    boxBg:   'bg-indigo-50',  boxText:   'text-indigo-600',
  },
  message: {
    icon:      <MessageCircle className="w-3.5 h-3.5" />,
    smallIcon: <MessageCircle className="w-3 h-3" />,
    pillBg:  'bg-teal-100',   pillText:  'text-teal-700',
    boxBg:   'bg-teal-50',    boxText:   'text-teal-600',
  },
};

// ── Small helpers ─────────────────────────────────────────────────────────────

function Spinner({ white }: { white?: boolean }) {
  return (
    <span className={`w-3 h-3 border-2 ${white ? 'border-white' : 'border-current'} border-t-transparent rounded-full animate-spin`} />
  );
}

function CategoryPill({ cat }: { cat: ApprovalCategory }) {
  const c = CAT_CONFIG[cat];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${c.pillBg} ${c.pillText}`}>
      {c.smallIcon}{CATEGORY_LABEL[cat]}
    </span>
  );
}

function CategoryBox({ cat }: { cat: ApprovalCategory }) {
  const c = CAT_CONFIG[cat];
  return (
    <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${c.boxBg} ${c.boxText}`}>
      {c.icon}
    </div>
  );
}

// ── Payment actions (role-based, multiple states) ─────────────────────────────

function PaymentActions({
  raw, opsUserId, client, qc,
}: {
  raw: PaymentVerificationItem;
  opsUserId: string;
  client: ReturnType<typeof createApiClient>;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const invalidate = () => qc.invalidateQueries({ queryKey: ['aq-payments'] });

  const advanceMut = useMutation({
    mutationFn: () => client.post(`/api/payments/${raw.paymentId}/advance-to-processing`),
    onSuccess: invalidate,
  });
  const approveMut = useMutation({
    mutationFn: () =>
      client.post('/api/payments/ops-approve', { paymentId: raw.paymentId, opsUserId }),
    onSuccess: invalidate,
  });
  const releaseMut = useMutation({
    mutationFn: () =>
      client.post('/api/payments/release', { paymentId: raw.paymentId, adminId: opsUserId }),
    onSuccess: invalidate,
  });

  const statusLabel = PAYMENT_STATUS_LABEL[raw.paymentStatus] ?? 'Unknown';

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
        raw.paymentStatus === 1 ? 'bg-gray-100 text-gray-600'
        : raw.paymentStatus === 2 ? 'bg-blue-100 text-blue-700'
        : raw.paymentStatus === 7 ? 'bg-indigo-100 text-indigo-700'
        : 'bg-gray-100 text-gray-500'
      }`}>{statusLabel}</span>

      {raw.paymentStatus === 1 && (
        <button
          onClick={() => advanceMut.mutate()}
          disabled={advanceMut.isPending}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg transition-colors"
        >
          {advanceMut.isPending ? <Spinner white /> : <Clock className="w-3 h-3" />}
          Advance
        </button>
      )}
      {raw.paymentStatus === 2 && (
        <button
          onClick={() => approveMut.mutate()}
          disabled={approveMut.isPending}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg transition-colors"
        >
          {approveMut.isPending ? <Spinner white /> : <CheckCircle className="w-3 h-3" />}
          Ops Approve
        </button>
      )}
      {raw.paymentStatus === 7 && (
        <button
          onClick={() => releaseMut.mutate()}
          disabled={releaseMut.isPending}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-lg transition-colors"
        >
          {releaseMut.isPending ? <Spinner white /> : <CheckCircle className="w-3 h-3" />}
          Release
        </button>
      )}
      {(advanceMut.isError || approveMut.isError || releaseMut.isError) && (
        <span title={(advanceMut.error ?? approveMut.error ?? releaseMut.error as Error)?.message}>
          <AlertCircle className="w-4 h-4 text-red-500" />
        </span>
      )}
    </div>
  );
}

// ── Per-item row with inline action panels ────────────────────────────────────

function ItemRow({
  item, opsUserId, client, qc,
}: {
  item: QueueItem;
  opsUserId: string;
  client: ReturnType<typeof createApiClient>;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [mode, setMode]           = useState<'idle' | 'reject' | 'approve-rating' | 'reply'>('idle');
  const [rejectReason, setRejectReason] = useState('');
  const [ratingValue, setRatingValue]   = useState(4);
  const [ratingText, setRatingText]     = useState('');
  const [replyText, setReplyText]       = useState('');

  const invalidateItem = useCallback(() => {
    const qkMap: Record<ApprovalCategory, string> = {
      document: 'aq-documents', rating: 'aq-ratings', verification: 'aq-verifications',
      credit: 'aq-credit', payment: 'aq-payments', message: 'aq-mailbox',
    };
    qc.invalidateQueries({ queryKey: [qkMap[item.category]] });
  }, [item.category, qc]);

  const approveMut = useMutation({
    mutationFn: () => {
      switch (item.category) {
        case 'document':
          return client.post(`/api/v1/documents/admin/${item.id}/approve`);
        case 'rating':
          return client.post(`/api/v1/ratings/screenshots/admin/${item.id}/approve`, {
            approvedRating: ratingValue,
            approvedReferenceText: ratingText || null,
          });
        case 'verification': {
          const raw = item.raw as PendingVerification;
          const endpoint = raw.roleName.toLowerCase() === 'workshop'
            ? `/api/v1/identity/admin/workshops/${item.userId}/approve`
            : `/api/v1/identity/admin/carriers/${item.userId}/approve`;
          return client.post(endpoint, { notes: '' });
        }
        case 'credit':
          return client.post(`/api/v1/users/${item.userId}/credit/approve`);
        default:
          return Promise.reject(new Error('No approve action'));
      }
    },
    onSuccess: () => { invalidateItem(); setMode('idle'); },
  });

  const rejectMut = useMutation({
    mutationFn: () => {
      switch (item.category) {
        case 'document':
          return client.post(`/api/v1/documents/admin/${item.id}/reject`, { reason: rejectReason });
        case 'rating':
          return client.post(`/api/v1/ratings/screenshots/admin/${item.id}/reject`, { reason: rejectReason });
        case 'verification': {
          const raw = item.raw as PendingVerification;
          const endpoint = raw.roleName.toLowerCase() === 'workshop'
            ? `/api/v1/identity/admin/workshops/${item.userId}/reject`
            : `/api/v1/identity/admin/carriers/${item.userId}/reject`;
          return client.post(endpoint, { notes: rejectReason });
        }
        case 'credit':
          return client.post(`/api/v1/users/${item.userId}/credit/reject`, { reason: rejectReason });
        default:
          return Promise.reject(new Error('No reject action'));
      }
    },
    onSuccess: () => { invalidateItem(); setMode('idle'); setRejectReason(''); },
  });

  const replyMut = useMutation({
    mutationFn: async () => {
      const raw = item.raw as MailboxItem;
      await client.post(`/api/v1/support/mailbox/${item.id}/reply`, {
        subject: `Re: ${raw.subject}`,
        bodyHtml: `<p>${replyText.replace(/\n/g, '<br>')}</p>`,
      });
      await client.post(`/api/v1/support/mailbox/${item.id}/send`);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['aq-mailbox'] }); setMode('idle'); setReplyText(''); },
  });

  const dismissMut = useMutation({
    mutationFn: () => client.delete(`/api/v1/support/mailbox/${item.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['aq-mailbox'] }),
  });

  const cat = CAT_CONFIG[item.category];

  return (
    <div className="px-4 py-3 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-start gap-3">
        <CategoryBox cat={item.category} />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{item.summary}</span>
            <CategoryPill cat={item.category} />
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {fmtDateTime(item.submittedAt)}
            {item.subType && item.subType !== CATEGORY_LABEL[item.category] && (
              <> · <span className="text-gray-500">{item.subType}</span></>
            )}
          </p>

          {/* Message body preview */}
          {item.category === 'message' && (item.raw as MailboxItem).bodyText && (
            <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded-lg p-2.5 border border-gray-100 line-clamp-3 whitespace-pre-wrap">
              {(item.raw as MailboxItem).bodyText!.slice(0, 400)}
            </p>
          )}

          {/* Payment summary line */}
          {item.category === 'payment' && (() => {
            const p = item.raw as PaymentVerificationItem;
            return (
              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-gray-500">
                <span>Gross: <span className="font-medium text-gray-700">{fmt(p.grossAmount)}</span></span>
                <span>Net: <span className="font-medium text-gray-700">{fmt(p.netAmount)}</span></span>
                <span>Customer: {p.customerFullName}</span>
                <span>Ref: {p.jobReferenceNumber}</span>
              </div>
            );
          })()}
        </div>

        {/* Action buttons (idle mode) */}
        {mode === 'idle' && (
          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            {item.category === 'payment' ? (
              <PaymentActions
                raw={item.raw as PaymentVerificationItem}
                opsUserId={opsUserId}
                client={client}
                qc={qc}
              />
            ) : item.category === 'message' ? (
              <>
                <button
                  onClick={() => setMode('reply')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />Reply
                </button>
                <button
                  onClick={() => dismissMut.mutate()}
                  disabled={dismissMut.isPending}
                  title="Dismiss"
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  {dismissMut.isPending ? <Spinner /> : <X className="w-3.5 h-3.5" />}
                </button>
              </>
            ) : item.category === 'rating' ? (
              <>
                <button
                  onClick={() => setMode('approve-rating')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Star className="w-3.5 h-3.5" />Approve
                </button>
                <button
                  onClick={() => setMode('reject')}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <XCircle className="w-3.5 h-3.5" />Reject
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => approveMut.mutate()}
                  disabled={approveMut.isPending}
                  title="Approve"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg transition-colors"
                >
                  {approveMut.isPending ? <Spinner white /> : <CheckCircle className="w-3.5 h-3.5" />}
                  Approve
                </button>
                <button
                  onClick={() => setMode('reject')}
                  title="Reject"
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold border border-red-200 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <XCircle className="w-3.5 h-3.5" />Reject
                </button>
                <button
                  title="Put on hold"
                  className="p-1.5 text-amber-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors"
                >
                  <Pause className="w-3.5 h-3.5" />
                </button>
              </>
            )}

            {(approveMut.isError || rejectMut.isError || dismissMut.isError) && (
              <span title={(approveMut.error ?? rejectMut.error ?? dismissMut.error as Error)?.message ?? 'Error'}>
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Reject panel ── */}
      {mode === 'reject' && (
        <div className={`mt-3 ml-10 p-3 rounded-xl border space-y-2 ${cat.pillBg} border-red-100 bg-red-50`}>
          <label className="block text-xs font-medium text-red-700">
            Rejection reason <span className="font-normal text-red-400">(shown to user)</span>
          </label>
          <textarea
            rows={2}
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="Explain why this is being rejected…"
            className="w-full border border-red-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-red-400 resize-none bg-white"
          />
          <div className="flex gap-2">
            <button
              onClick={() => rejectMut.mutate()}
              disabled={!rejectReason.trim() || rejectMut.isPending}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg transition-colors"
            >
              {rejectMut.isPending ? <Spinner white /> : <XCircle className="w-3 h-3" />}
              Confirm Reject
            </button>
            <button
              onClick={() => { setMode('idle'); setRejectReason(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-white transition-colors"
            >
              Cancel
            </button>
          </div>
          {rejectMut.isError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {(rejectMut.error as Error).message}
            </p>
          )}
        </div>
      )}

      {/* ── Rating approve panel ── */}
      {mode === 'approve-rating' && (
        <div className="mt-3 ml-10 p-3 bg-amber-50 rounded-xl border border-amber-100 space-y-2.5">
          <div>
            <label className="block text-xs font-medium text-amber-700 mb-1">Approved Rating</label>
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map(i => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setRatingValue(i)}
                  className="focus:outline-none"
                >
                  <Star className={`w-5 h-5 transition-colors ${i <= ratingValue ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                </button>
              ))}
              <span className="ml-2 text-xs text-gray-500">{ratingValue}/5</span>
            </div>
            {(item.raw as PendingRating).aiExtractedRating != null && (
              <p className="text-xs text-amber-600 mt-0.5">
                AI suggestion: {(item.raw as PendingRating).aiExtractedRating}/5
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-amber-700 mb-1">
              Reference text <span className="font-normal text-amber-500">(optional)</span>
            </label>
            <input
              type="text"
              value={ratingText}
              onChange={e => setRatingText(e.target.value)}
              placeholder="e.g. 4.8/5 on Trustpilot (1,240 reviews)"
              className="w-full border border-amber-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => approveMut.mutate()}
              disabled={approveMut.isPending}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-amber-600 hover:bg-amber-700 disabled:bg-amber-300 text-white rounded-lg transition-colors"
            >
              {approveMut.isPending ? <Spinner white /> : <CheckCircle className="w-3 h-3" />}
              Approve Rating
            </button>
            <button
              onClick={() => setMode('idle')}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-white transition-colors"
            >
              Cancel
            </button>
          </div>
          {approveMut.isError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {(approveMut.error as Error).message}
            </p>
          )}
        </div>
      )}

      {/* ── Reply panel ── */}
      {mode === 'reply' && (
        <div className="mt-3 ml-10 p-3 bg-blue-50 rounded-xl border border-blue-100 space-y-2">
          <p className="text-xs font-medium text-blue-700">
            Reply to: {(item.raw as MailboxItem).fromName ?? (item.raw as MailboxItem).fromAddress}
          </p>
          <textarea
            rows={3}
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Type your message to the user…"
            className="w-full border border-blue-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none bg-white"
          />
          <div className="flex gap-2">
            <button
              onClick={() => replyMut.mutate()}
              disabled={!replyText.trim() || replyMut.isPending}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg transition-colors"
            >
              {replyMut.isPending ? <Spinner white /> : <Send className="w-3 h-3" />}
              Send Reply
            </button>
            <button
              onClick={() => { setMode('idle'); setReplyText(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-white transition-colors"
            >
              Cancel
            </button>
          </div>
          {replyMut.isError && (
            <p className="text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {(replyMut.error as Error).message}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── User group row ─────────────────────────────────────────────────────────────

function UserGroupRow({
  group, expanded, onToggle, opsUserId, client, qc,
}: {
  group: UserGroup;
  expanded: boolean;
  onToggle: () => void;
  opsUserId: string;
  client: ReturnType<typeof createApiClient>;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [approvingAll, setApprovingAll] = useState(false);

  const autoApproveItems = group.items.filter(i =>
    i.category === 'document' || i.category === 'credit' || i.category === 'verification'
  );

  const approveAll = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setApprovingAll(true);
    try {
      await Promise.allSettled(autoApproveItems.map(item => {
        switch (item.category) {
          case 'document':
            return client.post(`/api/v1/documents/admin/${item.id}/approve`);
          case 'credit':
            return client.post(`/api/v1/users/${item.userId}/credit/approve`);
          case 'verification': {
            const raw = item.raw as PendingVerification;
            const endpoint = raw.roleName.toLowerCase() === 'workshop'
              ? `/api/v1/identity/admin/workshops/${item.userId}/approve`
              : `/api/v1/identity/admin/carriers/${item.userId}/approve`;
            return client.post(endpoint, { notes: '' });
          }
          default:
            return Promise.resolve();
        }
      }));
      qc.invalidateQueries({ queryKey: ['aq-documents'] });
      qc.invalidateQueries({ queryKey: ['aq-credit'] });
      qc.invalidateQueries({ queryKey: ['aq-verifications'] });
    } finally {
      setApprovingAll(false);
    }
  }, [autoApproveItems, client, qc]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Clickable header row */}
      <div
        className="flex items-center gap-4 px-4 py-3.5 cursor-pointer select-none hover:bg-gray-50/70 transition-colors"
        onClick={onToggle}
      >
        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm shrink-0 uppercase">
          {(group.userName || '?').charAt(0)}
        </div>

        {/* User */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">{group.userName || 'Unknown user'}</div>
          {group.userEmail && (
            <div className="text-xs text-gray-400 truncate">{group.userEmail}</div>
          )}
        </div>

        {/* Category pills */}
        <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
          {group.categories.map(cat => (
            <CategoryPill key={cat} cat={cat} />
          ))}
        </div>

        {/* Oldest date */}
        <div className="text-xs text-gray-400 whitespace-nowrap hidden md:block">
          {fmtDate(group.oldest)}
        </div>

        {/* Item count + chevron */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
            {group.items.length}
          </span>
          {expanded
            ? <ChevronUp className="w-4 h-4 text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {/* Expanded section */}
      {expanded && (
        <>
          {/* Approve All bar (only when multiple auto-approvable items) */}
          {autoApproveItems.length > 1 && (
            <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3">
              <span className="text-xs text-gray-500">
                {autoApproveItems.length} item{autoApproveItems.length > 1 ? 's' : ''} can be approved automatically
                <span className="text-gray-400"> (documents, credit, verifications)</span>
              </span>
              <button
                onClick={approveAll}
                disabled={approvingAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white rounded-lg transition-colors shrink-0"
              >
                {approvingAll ? <Spinner white /> : <CheckCircle className="w-3.5 h-3.5" />}
                Approve All ({autoApproveItems.length})
              </button>
            </div>
          )}

          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {group.items.map(item => (
              <ItemRow
                key={item.id}
                item={item}
                opsUserId={opsUserId}
                client={client}
                qc={qc}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminApprovalQueuePage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();
  const client = useMemo(() => createApiClient(getToken), [getToken]);

  const { isAdminOrOps, isRoleLoading } = useRole();
  const opsUserId = account?.userId ?? '';

  const [search, setSearch]               = useState('');
  const [catFilter, setCatFilter]         = useState<ApprovalCategory | 'all'>('all');
  const [expandedKey, setExpandedKey]     = useState<string | null>(null);

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { data: documents = [], isLoading: loadDocs, refetch: refetchDocs } = useQuery<PendingDoc[]>({
    queryKey: ['aq-documents'],
    queryFn: () =>
      client.get<PendingDoc[]>('/api/v1/documents/admin/pending', { params: { pageSize: 100 } }).then(r => r.data),
    enabled: !!account && isAdminOrOps,
    staleTime: 30_000,
  });

  const { data: ratings = [], isLoading: loadRatings, refetch: refetchRatings } = useQuery<PendingRating[]>({
    queryKey: ['aq-ratings'],
    queryFn: () =>
      client.get<PendingRating[]>('/api/v1/ratings/screenshots/admin/pending').then(r => r.data),
    enabled: !!account && isAdminOrOps,
    staleTime: 30_000,
  });

  const { data: verifications = [], isLoading: loadVer, refetch: refetchVer } = useQuery<PendingVerification[]>({
    queryKey: ['aq-verifications'],
    queryFn: () =>
      client.get<PendingVerification[]>('/api/v1/identity/admin/pending', { params: { pageSize: 100 } }).then(r => r.data),
    enabled: !!account && isAdminOrOps,
    staleTime: 30_000,
  });

  const { data: credits = [], isLoading: loadCredit, refetch: refetchCredit } = useQuery<CreditRequest[]>({
    queryKey: ['aq-credit'],
    queryFn: () =>
      client.get<CreditRequest[]>('/api/v1/users/credit/requests', {
        params: { status: 'Pending', pageSize: 100 },
      }).then(r => r.data),
    enabled: !!account && isAdminOrOps,
    staleTime: 30_000,
  });

  const { data: payments = [], isLoading: loadPayments, refetch: refetchPayments } = useQuery<PaymentVerificationItem[]>({
    queryKey: ['aq-payments'],
    queryFn: () =>
      client.get<PaymentVerificationItem[]>('/api/stripe-connect/payment-verification-queue', {
        params: { pageSize: 100 },
      }).then(r => r.data),
    enabled: !!account && isAdminOrOps,
    staleTime: 30_000,
  });

  const { data: mailboxData, isLoading: loadMailbox, refetch: refetchMailbox } = useQuery<{ items: MailboxItem[] }>({
    queryKey: ['aq-mailbox'],
    queryFn: () =>
      client.get<{ items: MailboxItem[] }>('/api/v1/support/mailbox', {
        params: { status: 0, pageSize: 100 },
      }).then(r => r.data),
    enabled: !!account && isAdminOrOps,
    staleTime: 30_000,
  });
  const mailbox = mailboxData?.items ?? [];

  const isLoading = loadDocs || loadRatings || loadVer || loadCredit || loadPayments || loadMailbox;

  const refetchAll = useCallback(async () => {
    await Promise.all([
      refetchDocs(), refetchRatings(), refetchVer(),
      refetchCredit(), refetchPayments(), refetchMailbox(),
    ]);
  }, [refetchDocs, refetchRatings, refetchVer, refetchCredit, refetchPayments, refetchMailbox]);

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(refetchAll);

  // ── Name lookup (enrich items that only have userId) ─────────────────────────

  const userNameMap = useMemo(() => {
    const map = new Map<string, { name: string; email: string }>();
    for (const v of verifications) map.set(v.userId, { name: v.fullName, email: v.email });
    for (const c of credits)       if (!map.has(c.userId)) map.set(c.userId, { name: c.fullName, email: c.email });
    for (const p of payments)      if (!map.has(p.carrierId)) map.set(p.carrierId, { name: p.carrierFullName, email: p.carrierEmail });
    return map;
  }, [verifications, credits, payments]);

  // ── Aggregate into user groups ───────────────────────────────────────────────

  const userGroups = useMemo((): UserGroup[] => {
    const map = new Map<string, UserGroup>();

    const upsert = (key: string, userId: string, defaultName: string, defaultEmail: string, item: QueueItem) => {
      if (!map.has(key)) {
        const lookup = userId ? userNameMap.get(userId) : undefined;
        map.set(key, {
          key, userId,
          userName:  lookup?.name  ?? defaultName,
          userEmail: lookup?.email ?? defaultEmail,
          items: [], categories: [], oldest: item.submittedAt,
        });
      }
      const g = map.get(key)!;
      if (userId && (!g.userEmail || g.userName === g.userId?.slice(0, 8))) {
        const lookup = userNameMap.get(userId);
        if (lookup) { g.userName = lookup.name; g.userEmail = lookup.email; }
      }
      g.items.push(item);
      if (!g.categories.includes(item.category)) g.categories.push(item.category);
      if (new Date(item.submittedAt) < new Date(g.oldest)) g.oldest = item.submittedAt;
    };

    // Documents
    for (const d of documents) {
      if (d.reviewStatus !== 'Pending') continue;
      if (!d.userId) continue;
      const label = DOC_LABELS[d.documentType] ?? d.documentType;
      upsert(d.userId, d.userId, d.userId.slice(0, 8), '', {
        id: d.documentId, category: 'document', subType: label,
        userId: d.userId, userName: d.userId.slice(0, 8), userEmail: '',
        summary: label, submittedAt: d.uploadedAt, raw: d,
      });
    }

    // Ratings
    for (const r of ratings) {
      if (r.status !== 'Pending') continue;
      if (!r.userId) continue;
      upsert(r.userId, r.userId, r.userId.slice(0, 8), '', {
        id: r.uploadId, category: 'rating', subType: r.platform,
        userId: r.userId, userName: r.userId.slice(0, 8), userEmail: '',
        summary: `${r.platform} rating screenshot`,
        submittedAt: r.uploadedAt, raw: r,
      });
    }

    // Verifications
    for (const v of verifications) {
      upsert(v.userId, v.userId, v.fullName, v.email, {
        id: `ver-${v.userId}`, category: 'verification', subType: v.roleName,
        userId: v.userId, userName: v.fullName, userEmail: v.email,
        summary: `${v.roleName} account verification${v.pendingDocuments > 0 ? ` · ${v.pendingDocuments} doc${v.pendingDocuments > 1 ? 's' : ''} pending` : ''}`,
        submittedAt: v.createdAt, raw: v,
      });
    }

    // Credits
    for (const c of credits) {
      if (c.creditApprovalStatus !== 'Pending') continue;
      upsert(c.userId, c.userId, c.fullName, c.email, {
        id: `credit-${c.userId}`, category: 'credit',
        subType: c.isBusinessAccount ? 'Business' : 'Personal',
        userId: c.userId, userName: c.fullName, userEmail: c.email,
        summary: c.isBusinessAccount
          ? `Business credit request${c.businessName ? ` — ${c.businessName}` : ''}`
          : 'Personal credit request',
        submittedAt: c.requestedAt ?? new Date(0).toISOString(), raw: c,
      });
    }

    // Payments
    for (const p of payments) {
      if (![1, 2, 7].includes(p.paymentStatus)) continue;
      upsert(p.carrierId, p.carrierId, p.carrierFullName, p.carrierEmail, {
        id: p.paymentId, category: 'payment',
        subType: PAYMENT_STATUS_LABEL[p.paymentStatus] ?? 'Unknown',
        userId: p.carrierId, userName: p.carrierFullName, userEmail: p.carrierEmail,
        summary: `${p.jobTitle} — ${fmt(p.grossAmount)}`,
        submittedAt: p.createdAt, raw: p,
      });
    }

    // Mailbox (chat + email)
    for (const m of mailbox) {
      const groupKey = m.userId ?? `email:${m.fromAddress}`;
      const userId   = m.userId ?? '';
      const userName = m.fromName ?? m.fromAddress;
      upsert(groupKey, userId, userName, m.fromAddress, {
        id: m.id, category: 'message',
        subType: m.source === 'Chatbot' ? 'Chat request' : 'Email',
        userId, userName, userEmail: m.fromAddress,
        summary: m.subject,
        submittedAt: m.receivedAt, raw: m,
      });
    }

    return Array.from(map.values())
      .sort((a, b) => new Date(a.oldest).getTime() - new Date(b.oldest).getTime());
  }, [documents, ratings, verifications, credits, payments, mailbox, userNameMap]);

  // ── Filters ──────────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    return userGroups.filter(g => {
      if (catFilter !== 'all' && !g.categories.includes(catFilter)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!g.userName.toLowerCase().includes(q) && !g.userEmail.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [userGroups, catFilter, search]);

  const totalPending = userGroups.reduce((s, g) => s + g.items.length, 0);

  const categoryCounts = useMemo(() => {
    const m: Partial<Record<ApprovalCategory, number>> = {};
    for (const g of userGroups) {
      for (const cat of g.categories) {
        m[cat] = (m[cat] ?? 0) + g.items.filter(i => i.category === cat).length;
      }
    }
    return m;
  }, [userGroups]);

  // ── Access guard ──────────────────────────────────────────────────────────────

  if (isRoleLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdminOrOps) {
    return (
      <div className="page-shell text-center text-gray-400">
        <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm font-medium">Access restricted to Admin and Ops users.</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="page-shell">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approval Queue</h1>
          <p className="mt-1 text-sm text-gray-500">
            All pending items across documents, ratings, verifications, payments and messages.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {totalPending > 0 && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-full text-sm font-semibold">
              <Clock className="w-4 h-4" />
              {totalPending} pending
            </span>
          )}
          <RefreshButton
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            lastRefreshed={lastRefreshed}
          />
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        {/* Search */}
        <div className="relative min-w-[200px] max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap items-center gap-1.5">
          {(['all', 'document', 'rating', 'verification', 'credit', 'payment', 'message'] as const).map(cat => {
            const active = catFilter === cat;
            const count  = cat === 'all' ? totalPending : (categoryCounts[cat as ApprovalCategory] ?? 0);
            return (
              <button
                key={cat}
                onClick={() => setCatFilter(cat)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  active
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {cat === 'all'
                  ? <Inbox className="w-3.5 h-3.5" />
                  : CAT_CONFIG[cat as ApprovalCategory].smallIcon}
                {CATEGORY_LABEL[cat]}
                {count > 0 && (
                  <span className={`font-bold ${active ? 'text-white/80' : 'text-gray-400'}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin w-7 h-7 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-medium">
            {totalPending === 0
              ? 'All caught up! No pending approvals.'
              : 'No results match your current filter.'}
          </p>
          {totalPending === 0 && (
            <p className="text-xs mt-1 text-gray-400">Everything looks good across all categories.</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(group => (
            <UserGroupRow
              key={group.key}
              group={group}
              expanded={expandedKey === group.key}
              onToggle={() => setExpandedKey(k => k === group.key ? null : group.key)}
              opsUserId={opsUserId}
              client={client}
              qc={qc}
            />
          ))}
        </div>
      )}
    </div>
  );
}
